import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Input } from "@/components/ui/inputForm";
import { Button } from "@/components/ui/button";
import { message, Select, ConfigProvider, theme as antdTheme, Checkbox } from "antd";
import { getUsuarioById, postUsuario } from "@/services/usuario";
import { getUnidades } from "@/services/unidade";
import { getPerfis } from "@/services/perfil";
import { UsuarioType } from "@/types/UsuarioType";
import { PerfilType } from "@/types/PerfilType";
import { UnidadeType } from "@/types/UnidadeType";
import { usePermissions } from "@/hooks/usePermissions";
import { errorHandler } from "@/utils/errorhandler";
import { PERFIL_MASTER, isPerfilMaster } from "@/utils/perfis";

const defaultValues = {
  nome: "",
  email: "",
  password: "",
  ativo: true,
  acessoRecebimentoFinanceiro: false,
  unidades: [],
  perfil: undefined,
};

export default function CadastroUsuario() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [units, setUnits] = useState<UnidadeType[]>([]);
  const [profiles, setProfiles] = useState<PerfilType[]>([]);
  const [loading, setLoading] = useState(false);
  const { isMaster } = usePermissions();

  // Só o MASTER cadastra/edita usuários com o perfil MASTER: para os demais o
  // perfil nem aparece no select (a API bloqueia o mesmo cenário no POST).
  const perfisDisponiveis = isMaster
    ? profiles
    : profiles.filter((p) => !isPerfilMaster(p));

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    formState: { errors },
    watch,
  } = useForm<UsuarioType>({
    defaultValues,
    mode: "onBlur",
  });

  useEffect(() => {
    getUnidades().then((res) => {
      if (res.status === 200) setUnits(res.data);
    });
    getPerfis().then((res) => {
      if (res.status === 200) setProfiles(res.data);
    });
  }, []);

  useEffect(() => {
    if (isEdit && id) {
      getUsuarioById(id).then((res) => {
        if (res.status === 200) {
          const usuario = res.data;
          if (isPerfilMaster(usuario.perfil) && !isMaster) {
            message.error(
              `Somente usuários com perfil ${PERFIL_MASTER} podem editar usuários com este perfil.`
            );
            navigate("/usuario");
            return;
          }
          reset({
            ...defaultValues,
            ...usuario,
            perfil: usuario.perfil?.id,
            unidades: usuario.unidades?.map((u: any) => u.id) || [],
            ativo: usuario.ativo !== false,
          });
        }
      });
    } else {
      reset(defaultValues);
    }
  }, [id, isEdit, reset, isMaster, navigate]);

  const onSubmit = async (data: any) => {
    const perfilSelecionado = profiles.find((p) => String(p.id) === String(data.perfil));
    if (isPerfilMaster(perfilSelecionado) && !isMaster) {
      message.error(
        `Somente usuários com perfil ${PERFIL_MASTER} podem cadastrar usuários com este perfil.`
      );
      return;
    }
    try {
      setLoading(true);
      const payload = {
        ...data,
        perfilId: data.perfil,
        unidadesIds: data.unidades,
        ativo: !!data.ativo,
        id: id || undefined,
      };
      const response = await postUsuario(payload);
      if (response.status === 200 || response.status === 201) {
        message.success("Usuário salvo com sucesso!");
        navigate("/usuario");
      }
    } catch (error: any) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-8">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-8">
          <span className="text-white text-2xl">{isEdit ? "Editar Usuário" : "Cadastro de Usuário"}</span>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div className="col-span-2">
            <label>Nome *</label>
            <Input type="text" placeholder="Nome" {...register("nome", { required: "Nome é obrigatório" })} />
            {errors.nome && <span className="text-red-600">{errors.nome.message}</span>}
          </div>
          <div className="col-span-2">
            <label>E-mail *</label>
            <Input type="email" placeholder="E-mail" {...register("email", { required: "E-mail é obrigatório" })} />
            {errors.email && <span className="text-red-600">{errors.email.message}</span>}
          </div>
          <div>
            <label>Senha {!isEdit && "*"}</label>
            <Input type="password" placeholder={isEdit ? "Bloqueado — use 'Alterar Senha' na listagem" : "Senha"} disabled={isEdit} {...register("password", { required: !isEdit && "Senha é obrigatória" })} />
            {errors.password && <span className="text-red-600">{errors.password.message}</span>}
          </div>
          <div className="flex items-center mt-6">
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm] }}>
              <Controller
                control={control}
                name="ativo"
                render={({ field }) => (
                  <Checkbox
                    id="ativo"
                    checked={!!field.value}
                    onChange={e => field.onChange(e.target.checked)}
                  >
                    <label htmlFor="ativo" className="text-white ml-2">Ativo</label>
                  </Checkbox>
                )}
              />
            </ConfigProvider>
          </div>
          <div className="flex items-center mt-6">
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm] }}>
              <Controller
                control={control}
                name="acessoRecebimentoFinanceiro"
                render={({ field }) => (
                  <Checkbox
                    id="acessoRecebimentoFinanceiro"
                    checked={!!field.value}
                    onChange={e => field.onChange(e.target.checked)}
                  >
                    <label htmlFor="acessoRecebimentoFinanceiro" className="text-white ml-2">Acesso ao recebimento financeiro</label>
                  </Checkbox>
                )}
              />
            </ConfigProvider>
          </div>
          <div className="col-span-2">
            <label>Unidades *</label>
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm] }}>
              <Controller
                control={control}
                name="unidades"
                rules={{ required: "Selecione ao menos uma unidade" }}
                render={({ field }) => (
                  <Select
                    mode="multiple"
                    allowClear
                    className="w-full h-10"
                    placeholder="Selecione as unidades"
                    value={field.value}
                    onChange={field.onChange}
                    options={units.map((u) => ({ value: u.id, label: u.nome }))}
                  />
                )}
              />
            </ConfigProvider>
            {errors.unidades && <span className="text-red-600">{errors.unidades.message}</span>}
          </div>
          <div className="col-span-2">
            <label>Perfil *</label>
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm] }}>
              <Controller
                control={control}
                name="perfil"
                rules={{ required: "Selecione um perfil" }}
                render={({ field }) => (
                  <Select
                    allowClear
                    className="w-full h-10"
                    placeholder="Selecione o perfil"
                    value={field.value}
                    onChange={field.onChange}
                    options={perfisDisponiveis.map((p) => ({ value: p.id, label: p.nome }))}
                  />
                )}
              />
            </ConfigProvider>
            {errors.perfil && <span className="text-red-600">{errors.perfil.message}</span>}
          </div>
        </div>
        <div className="flex justify-start p-2 mt-12 gap-8">
          <Button
            className="flex gap-2"
            variant="destructive"
            type="button"
            onClick={() => navigate("/usuario")}
          >
            Cancelar
          </Button>
          <Button className="flex gap-2" type="submit">
            {loading ? (
              <span className="animate-spin mr-2">⏳</span>
            ) : null}
            Salvar
          </Button>
        </div>
      </form>
    </div>
  );
}
